import React, { useEffect, useRef, useState } from "react";
import AppLayout from "components/ui/AppLayout";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import { inboxApi } from "api/inbox";
import { approvalsApi } from "api/approvals";
import { callsApi } from "api/calls";
import { followUpsApi } from "api/followUps";
import { orgApi } from "api/org";
import { getCurrentUser } from "api/client";

function formatTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function statusChip(status) {
  if (status === "closed") return { label: "Closed", bg: "#FEE2E2", color: "#B91C1C" };
  return { label: "Open", bg: "#DCFCE7", color: "#15803D" };
}

const DRAFT_KEY = "crm_inbox_draft";

/**
 * Inbox (Sprint 2, Part 55 / Part 72 §72.4): thread list + conversation +
 * composer. Draft is kept locally (draft-before-send — never sent until the
 * user presses Send). No fake data — everything comes from the NestJS API.
 */
export default function Inbox() {
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [draft, setDraft] = useState(() => localStorage.getItem(DRAFT_KEY) || "");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [sendError, setSendError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [suggestions, setSuggestions] = useState([]);
  const [calls, setCalls] = useState([]);
  const [aiError, setAiError] = useState("");
  const [suggestionBusy, setSuggestionBusy] = useState(null);
  const [businessHours, setBusinessHours] = useState(null);
  const [whatsappPhone, setWhatsappPhone] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Sprint 15 QA: connection banner — the number customer messages land on.
    orgApi
      .getWhatsappStatus()
      .then((s) => setWhatsappPhone(s?.phone ?? null))
      .catch(() => setWhatsappPhone(null));
  }, []);

  const loadThreads = async () => {
    try {
      const data = await inboxApi.conversations({ status: statusFilter });
      setThreads(data.items);
      // Sprint 9 (Part 89 §89.5): business-hours state for the header badge.
      setBusinessHours(data.businessHours);
      setLoadError("");
    } catch (error) {
      setLoadError(error?.message || "Unable to load conversations.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadThreads();
    inboxApi.templates().then(setTemplates).catch(() => setTemplates([]));
  }, [statusFilter]);

  // Sprint 14 (Part 94 §94.3): one-tap refresh — threads + active thread.
  const [isRefreshing, setIsRefreshing] = useState(false);
  const refreshAll = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        loadThreads(),
        activeThread ? inboxApi.messages(activeThread.id).then(setMessages) : Promise.resolve(),
        activeThread ? loadSuggestions(activeThread.id) : Promise.resolve(),
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const loadSuggestions = async (conversationId) => {
    try {
      const items = await approvalsApi.list({ entityType: "conversation", entityId: conversationId });
      setSuggestions(items.filter((s) => s.status === "pending"));
      setAiError("");
    } catch (error) {
      setAiError(error?.message || "Unable to load AI suggestions.");
    }
  };

  useEffect(() => {
    if (!activeThread) {
      setMessages([]);
      setSuggestions([]);
      return;
    }
    inboxApi
      .messages(activeThread.id)
      .then((items) => {
        setMessages(items);
        // Reading the thread clears unread + the badge (Sprint 15 QA).
        return inboxApi.markRead(activeThread.id).then(() => loadThreads());
      })
      .catch((error) => setLoadError(error?.message || "Unable to load messages."));
    loadSuggestions(activeThread.id);
    callsApi
      .listByContact(activeThread.contactId)
      .then(setCalls)
      .catch(() => setCalls([]));
  }, [activeThread]);

  const [isDrafting, setIsDrafting] = useState(false);

  const handleDraftFollowUp = async () => {
    if (!activeThread || isDrafting) return;
    setIsDrafting(true);
    setAiError("");
    try {
      await followUpsApi.draft({ entityType: "conversation", entityId: activeThread.id });
      await loadSuggestions(activeThread.id);
    } catch (error) {
      setAiError(error?.message || "Could not create the follow-up draft.");
    } finally {
      setIsDrafting(false);
    }
  };

  // Sprint 9 (Part 89 §89.5): claim an unassigned thread (support SLA surface).
  const handleClaim = async (thread) => {
    const me = getCurrentUser();
    if (!me?.sub) return;
    try {
      const updated = await inboxApi.update(thread.id, { assignedUserId: me.sub });
      setThreads((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
      if (activeThread?.id === thread.id) setActiveThread((prev) => ({ ...prev, ...updated }));
    } catch (error) {
      setLoadError(error?.message || "Could not claim the conversation.");
    }
  };

  const handleSuggestion = async (suggestionId, action) => {
    setSuggestionBusy({ id: suggestionId, action });
    try {
      if (action === "approve") await approvalsApi.approve(suggestionId);
      else await approvalsApi.reject(suggestionId);
      setSuggestions((prev) => prev.filter((s) => s.id !== suggestionId));
      if (activeThread) {
        const items = await inboxApi.messages(activeThread.id);
        setMessages(items);
      }
    } catch (error) {
      setAiError(error?.message || `Could not ${action} the suggestion.`);
    } finally {
      setSuggestionBusy(null);
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const selectThread = (thread) => {
    setActiveThread(thread);
    setMessages([]);
  };

  const handleSend = async () => {
    if (!activeThread || !draft.trim() || isSending) return;
    setSendError("");
    setIsSending(true);
    try {
      await inboxApi.send(activeThread.id, {
        content: draft.trim(),
        templateName: selectedTemplate || undefined,
      });
      setDraft("");
      setSelectedTemplate("");
      localStorage.removeItem(DRAFT_KEY);
      await loadThreads();
      const items = await inboxApi.messages(activeThread.id);
      setMessages(items);
    } catch (error) {
      setSendError(error?.message || "Message could not be sent.");
    } finally {
      setIsSending(false);
    }
  };

  const handleDraftChange = (value) => {
    setDraft(value);
    localStorage.setItem(DRAFT_KEY, value);
  };

  const handleCloseThread = async () => {
    if (!activeThread) return;
    try {
      const updated = await inboxApi.update(activeThread.id, { status: "closed" });
      setThreads((prev) => prev.map((t) => (t.id === updated.id ? { ...t, status: updated.status } : t)));
      setActiveThread((prev) => (prev ? { ...prev, status: "closed" } : prev));
    } catch (error) {
      setSendError(error?.message || "Could not update the thread.");
    }
  };

  return (
    <AppLayout>
      <div className="px-4 md:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading font-bold text-2xl md:text-3xl" style={{ color: "var(--color-foreground)", letterSpacing: "-0.02em" }}>
              Inbox
            </h1>
            <p className="font-caption text-sm mt-1" style={{ color: "var(--color-text-secondary)" }}>
              WhatsApp conversations — reply with consent, close on STOP
            </p>
            {businessHours && (
              <span
                className="inline-flex items-center gap-1.5 mt-1.5 px-2.5 py-1 rounded-full font-caption text-xs font-medium"
                style={{ background: businessHours.openNow ? "#DCFCE7" : "#FEF3C7", color: businessHours.openNow ? "#15803D" : "#B45309" }}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: businessHours.openNow ? "#16A34A" : "#F59E0B" }} />
                {businessHours.openNow
                  ? "In business hours — replies expected within minutes"
                  : "Outside hours (9am–9pm IST) — auto-reply sent, you reply at open"}
              </span>
            )}
            {whatsappPhone === null && (
              <p
                className="inline-flex items-center gap-1.5 mt-1.5 px-2.5 py-1 rounded-full font-caption text-xs font-medium"
                style={{ background: "#FEF3C7", color: "#B45309" }}
              >
                <Icon name="AlertTriangle" size={12} color="currentColor" />
                WhatsApp not connected — add your number in Settings → WhatsApp channel
              </p>
            )}
          </div>
<div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={refreshAll} disabled={isRefreshing} loading={isRefreshing}>
              <Icon name="RefreshCw" size={13} style={{ marginRight: 5 }} />
              Refresh
            </Button>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-sm px-3 py-2 rounded-lg border font-caption"
              style={{ borderColor: "var(--color-border)", background: "var(--color-card)", color: "var(--color-foreground)", height: 38 }}
            >
            <option value="all">All threads</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </select>
          </div>
        </div>

        {loadError && (
          <div className="mb-4 rounded-lg border px-4 py-3" style={{ borderColor: "var(--color-error)", background: "var(--color-card)" }}>
            <p className="font-caption text-sm" style={{ color: "var(--color-error)" }}>{loadError}</p>
          </div>
        )}

        {isLoading ? (
          <div className="rounded-xl border px-4 py-10 text-center" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
            <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>Loading conversations...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" style={{ minHeight: 560 }}>
            {/* Thread list */}
            <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
              <div className="px-4 py-3 border-b" style={{ borderColor: "var(--color-border)" }}>
                <p className="font-caption text-sm font-semibold" style={{ color: "var(--color-foreground)" }}>
                  Conversations <span className="text-xs font-normal" style={{ color: "var(--color-text-secondary)" }}>({threads.length})</span>
                </p>
              </div>
              <div className="divide-y max-h-[560px] overflow-y-auto" style={{ borderColor: "var(--color-border)" }}>
                {threads.length === 0 && (
                  <div className="px-4 py-10 text-center">
                    <Icon name="MessageSquare" size={18} color="var(--color-text-secondary)" className="mx-auto mb-2" />
                    <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>
                      No conversations yet. Inbound WhatsApp messages land here.
                    </p>
                  </div>
                )}
                {threads.map((thread) => {
                  const chip = statusChip(thread.status);
                  const active = activeThread?.id === thread.id;
                  return (
                    <button
                      key={thread.id}
                      onClick={() => selectThread(thread)}
                      className="w-full text-left px-4 py-3 transition-colors hover:bg-muted"
                      style={{ background: active ? "var(--color-muted)" : "transparent" }}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-caption text-sm font-semibold truncate" style={{ color: "var(--color-foreground)" }}>
                          {thread.contactName}
                        </span>
                        <span
                          className="text-xs font-caption px-2 py-0.5 rounded-full flex-shrink-0"
                          style={{ background: chip.bg, color: chip.color }}
                        >
                          {chip.label}
                        </span>
                      </div>
                      <p className="font-caption text-xs mt-1 truncate" style={{ color: "var(--color-text-secondary)" }}>
                        {thread.snippet || "No messages yet"}
                      </p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="font-caption text-[11px]" style={{ color: "var(--color-text-secondary)" }}>
                          {formatTime(thread.lastMessageAtUtc)}
                        </span>
                        {!thread.assignedUserId && thread.status === "open" && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClaim(thread);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.stopPropagation();
                                handleClaim(thread);
                              }
                            }}
                            className="flex items-center gap-1 font-caption text-[11px] font-medium px-2 py-0.5 rounded-full hover:bg-muted"
                            style={{ color: "var(--color-primary)" }}
                          >
                            <Icon name="UserPlus" size={10} color="currentColor" />
                            Claim
                          </span>
                        )}
                        {thread.unreadCount > 0 && (
                          <span className="flex items-center justify-center rounded-full text-[11px] font-semibold min-w-[18px] h-[18px] px-1" style={{ background: "var(--color-primary)", color: "#fff" }}>
                            {thread.unreadCount}
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Conversation pane */}
            <div className="lg:col-span-2 rounded-xl border overflow-hidden flex flex-col" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
              {!activeThread ? (
                <div className="flex-1 flex flex-col items-center justify-center gap-3 py-20">
                  <Icon name="MessageCircle" size={22} color="var(--color-text-secondary)" />
                  <p className="font-caption text-sm" style={{ color: "var(--color-text-secondary)" }}>
                    Select a conversation to view and reply
                  </p>
                </div>
              ) : (
                <>
                  {/* Thread header */}
                  <div className="px-4 py-3 border-b flex items-center justify-between gap-3" style={{ borderColor: "var(--color-border)" }}>
                    <div>
                      <p className="font-caption text-sm font-semibold" style={{ color: "var(--color-foreground)" }}>
                        {activeThread.contactName}
                      </p>
                      <p className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>
                        {activeThread.status === "closed"
                          ? "Closed — contact opted out"
                          : activeThread.assignedUserId
                            ? "Assigned to you — this thread is yours to answer"
                            : "Unassigned — claim it to own the reply"}
                      </p>
                    </div>
                    {activeThread.status !== "closed" && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleDraftFollowUp}
                          disabled={isDrafting}
                          className="flex items-center gap-1.5 text-xs font-caption px-3 py-1.5 rounded-lg border transition-colors hover:bg-muted"
                          style={{ borderColor: "var(--color-border)", color: "var(--color-primary)" }}
                        >
                          <Icon name="Sparkles" size={12} color="currentColor" />
                          {isDrafting ? "Drafting..." : "Draft follow-up"}
                        </button>
                        <button
                          onClick={handleCloseThread}
                          className="flex items-center gap-1.5 text-xs font-caption px-3 py-1.5 rounded-lg border transition-colors hover:bg-muted"
                          style={{ borderColor: "var(--color-border)", color: "var(--color-text-secondary)" }}
                        >
                          <Icon name="Lock" size={12} color="currentColor" />
                          Close thread
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Call timeline (Sprint 4) */}
                  {calls.length > 0 && (
                    <div className="px-4 py-2.5 border-b space-y-2" style={{ borderColor: "var(--color-border)" }}>
                      <div className="flex items-center gap-1.5">
                        <Icon name="Phone" size={12} color="var(--color-text-secondary)" />
                        <span className="font-caption text-xs font-semibold" style={{ color: "var(--color-foreground)" }}>
                          Calls ({calls.length})
                        </span>
                      </div>
                      {calls.map((call) => (
                        <div key={call.id} className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2" style={{ borderColor: "var(--color-border)", background: "var(--color-background)" }}>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-caption text-xs font-semibold" style={{ color: "var(--color-foreground)" }}>
                                {call.direction === "inbound" ? "Inbound" : "Outbound"} call
                              </span>
                              {call.missed && (
                                <span className="font-caption text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: "#FEE2E2", color: "#B91C1C" }}>
                                  Missed
                                </span>
                              )}
                              <span className="font-caption text-[11px]" style={{ color: "var(--color-text-secondary)" }}>
                                {call.durationSec ? `${Math.round(call.durationSec / 60)} min` : "—"}
                              </span>
                            </div>
                            {call.transcriptState === "ready" && call.transcript && (
                              <p className="font-caption text-[11px] mt-1 truncate" style={{ color: "var(--color-text-secondary)" }}>
                                {call.transcript.slice(0, 110)}…
                              </p>
                            )}
                            {call.transcriptState === "consent_denied" && (
                              <p className="font-caption text-[11px] mt-1" style={{ color: "var(--color-text-secondary)" }}>
                                Recording without disclosure consent — no transcript.
                              </p>
                            )}
                            {call.aiSummary && (
                              <p className="font-caption text-[11px] mt-1" style={{ color: "var(--color-primary)" }}>
                                <Icon name="Sparkles" size={10} color="currentColor" className="inline mr-1" />
                                {call.aiIntent?.toUpperCase()} · {Math.round((call.aiIntentConfidence || 0) * 100)}% — {call.aiSummary.slice(0, 90)}…
                              </p>
                            )}
                          </div>
                          <span className="font-caption text-[11px] flex-shrink-0" style={{ color: "var(--color-text-secondary)" }}>
                            {formatTime(call.createdAtUtc)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* AI suggestions (Sprint 3) */}
                  {suggestions.length > 0 && (
                    <div className="px-4 py-2.5 border-b space-y-2" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
                      <div className="flex items-center gap-1.5">
                        <Icon name="Sparkles" size={12} color="var(--color-primary)" />
                        <span className="font-caption text-xs font-semibold" style={{ color: "var(--color-foreground)" }}>
                          AI detected
                        </span>
                      </div>
                      {suggestions.map((s) => (
                        <div key={s.id} className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2" style={{ borderColor: "var(--color-border)", background: "var(--color-background)" }}>
                          <div className="min-w-0">
                            <p className="font-caption text-xs truncate" style={{ color: "var(--color-foreground)" }}>
                              <span className="font-semibold">{labelFor(s.field)}:</span> {displayValue(s.suggestedValue)}
                            </p>
                            <p className="font-caption text-[11px] mt-0.5" style={{ color: "var(--color-text-secondary)" }}>
                              Confidence {Math.round(s.confidence * 100)}% · {s.sourceRef}
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <button
                              onClick={() => handleSuggestion(s.id, "approve")}
                              disabled={suggestionBusy?.id === s.id}
                              className="flex items-center gap-1 px-2.5 py-1 rounded-md font-caption text-xs font-semibold disabled:opacity-50"
                              style={{ background: "#DCFCE7", color: "#15803D" }}
                            >
                              <Icon name="Check" size={11} color="currentColor" />
                              {suggestionBusy?.id === s.id && suggestionBusy?.action === "approve" ? "Saving…" : "Approve"}
                            </button>
                            <button
                              onClick={() => handleSuggestion(s.id, "reject")}
                              disabled={suggestionBusy?.id === s.id}
                              className="flex items-center gap-1 px-2.5 py-1 rounded-md font-caption text-xs font-semibold disabled:opacity-50"
                              style={{ background: "#FEE2E2", color: "#B91C1C" }}
                            >
                              <Icon name="X" size={11} color="currentColor" />
                              {suggestionBusy?.id === s.id && suggestionBusy?.action === "reject" ? "Saving…" : "Reject"}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {aiError && (
                    <div className="px-4 py-2 border-b" style={{ borderColor: "var(--color-border)" }}>
                      <p className="font-caption text-[11px]" style={{ color: "var(--color-error)" }}>{aiError}</p>
                    </div>
                  )}

                  {/* Messages */}
                  <div className="flex-1 px-4 py-4 overflow-y-auto space-y-3" style={{ maxHeight: 420 }}>
                    {messages.length === 0 && (
                      <p className="font-caption text-sm text-center" style={{ color: "var(--color-text-secondary)" }}>
                        No messages in this thread yet.
                      </p>
                    )}
                    {messages.map((message) => {
                      const inbound = message.direction === "inbound";
                      const showAi = inbound && message.aiProcessingState !== "none";
                      return (
                        <div key={message.id} className={`flex flex-col ${inbound ? "items-start" : "items-end"}`}>
                          <div className={`flex ${inbound ? "justify-start" : "justify-end"} w-full`}>
                          <div
                            className="max-w-[75%] rounded-xl px-3.5 py-2.5"
                            style={{
                              background: inbound ? "var(--color-muted)" : "var(--color-primary)",
                              borderTopLeftRadius: inbound ? 4 : 12,
                              borderTopRightRadius: inbound ? 12 : 4,
                            }}
                          >
                            <p className="font-caption text-sm whitespace-pre-wrap" style={{ color: inbound ? "var(--color-foreground)" : "#FFFFFF" }}>
                              {message.content}
                            </p>
                            <div className={`flex items-center gap-1.5 mt-1 ${inbound ? "" : "justify-end"}`}>
                              <span className="font-caption text-[11px]" style={{ color: inbound ? "var(--color-text-secondary)" : "rgba(255,255,255,0.75)" }}>
                                {formatTime(message.atUtc)}
                              </span>
                              {!inbound && message.templateName && (
                                <span className="font-caption text-[11px] px-1.5 py-0.5 rounded-full" style={{ background: "rgba(255,255,255,0.2)", color: "#fff" }}>
                                  {message.templateName}
                                </span>
                              )}
                              {!inbound && (
                                <Icon name={message.status === "failed" ? "XCircle" : message.status === "read" ? "CheckCheck" : ["sent", "delivered"].includes(message.status) ? "Check" : "Clock"} size={11} color={message.status === "failed" ? "#FCA5A5" : "rgba(255,255,255,0.75)"} />
                              )}
                            </div>
                          </div>
                          </div>
                          {/* AI extraction (Sprint 3) — async, never blocks the thread */}
                          {showAi && (
                            <div className="mt-1 max-w-[85%]">
                              {message.aiProcessingState === "processing" && (
                                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
                                  <span className="w-3 h-3 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: "var(--color-primary)", borderTopColor: "transparent" }} />
                                  <span className="font-caption text-[11px]" style={{ color: "var(--color-text-secondary)" }}>AI processing…</span>
                                </div>
                              )}
                              {message.aiProcessingState === "failed" && (
                                <div className="px-2.5 py-1.5 rounded-lg border" style={{ borderColor: "#FCA5A5", background: "#FEF2F2" }}>
                                  <span className="font-caption text-[11px]" style={{ color: "#B91C1C" }}>AI extraction failed — message stays available.</span>
                                </div>
                              )}
                              {(message.aiProcessingState === "ready" || message.aiProcessingState === "skipped") && (
                                <div className="px-2.5 py-1.5 rounded-lg border" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
                                  {message.aiProcessingState === "skipped" ? (
                                    <span className="font-caption text-[11px]" style={{ color: "var(--color-text-secondary)" }}>
                                      AI skipped (short / opt-out message)
                                    </span>
                                  ) : (
                                    <>
                                      <div className="flex items-center gap-1.5 mb-1">
                                        <Icon name="Sparkles" size={11} color="var(--color-primary)" />
                                        <span className="font-caption text-[11px] font-semibold uppercase" style={{ color: "var(--color-primary)" }}>
                                          {message.aiIntent || "other"} · {Math.round((message.aiIntentConfidence || 0) * 100)}%
                                        </span>
                                      </div>
                                      <p className="font-caption text-[11px]" style={{ color: "var(--color-text-secondary)" }}>
                                        {message.aiSummary}
                                      </p>
                                    </>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Composer */}
                  <div className="px-4 py-3 border-t space-y-2" style={{ borderColor: "var(--color-border)" }}>
                    {templates.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Icon name="FileText" size={13} color="var(--color-text-secondary)" />
                        <select
                          value={selectedTemplate}
                          onChange={(e) => setSelectedTemplate(e.target.value)}
                          className="text-xs px-2 py-1.5 rounded-md border font-caption flex-1"
                          style={{ borderColor: "var(--color-border)", background: "var(--color-card)", color: "var(--color-foreground)" }}
                          aria-label="Choose a template"
                        >
                          <option value="">Plain message (no template)</option>
                          {templates.map((t) => (
                            <option key={t.name} value={t.name} title={t.body}>
                              {t.name} · {t.category}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="flex items-end gap-2">
                      <textarea
                        rows={2}
                        placeholder={activeThread.status === "closed" ? "Thread closed — contact opted out" : "Type a reply... (draft is saved locally)"}
                        value={draft}
                        disabled={activeThread.status === "closed"}
                        onChange={(e) => handleDraftChange(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSend();
                          }
                        }}
                        className="flex-1 rounded-lg border px-3 py-2 font-caption text-sm resize-none"
                        style={{
                          borderColor: "var(--color-border)",
                          background: "var(--color-background)",
                          color: "var(--color-foreground)",
                          outline: "none",
                        }}
                      />
                      <button
                        onClick={handleSend}
                        disabled={isSending || !draft.trim() || activeThread.status === "closed"}
                        className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-caption text-sm font-semibold"
                        style={{
                          background: isSending || !draft.trim() || activeThread.status === "closed" ? "var(--color-muted)" : "var(--color-primary)",
                          color: isSending || !draft.trim() || activeThread.status === "closed" ? "var(--color-text-secondary)" : "#FFFFFF",
                          cursor: isSending || !draft.trim() || activeThread.status === "closed" ? "not-allowed" : "pointer",
                        }}
                      >
                        {isSending ? (
                          <span className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: "currentColor", borderTopColor: "transparent" }} />
                        ) : (
                          <Icon name="Send" size={14} color="currentColor" />
                        )}
                        Send
                      </button>
                    </div>
                    {sendError && (
                      <p className="font-caption text-xs" style={{ color: "var(--color-error)" }}>{sendError}</p>
                    )}
                    {draft && (
                      <p className="font-caption text-[11px]" style={{ color: "var(--color-text-secondary)" }}>
                        Draft saved locally · Ctrl/Cmd+Enter or Send to deliver
                      </p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-4 mt-4 border-t" style={{ borderColor: "var(--color-border)" }}>
          <p className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>
            &copy; {new Date().getFullYear()} CRMAnalytics. All rights reserved.
          </p>
          <p className="font-caption text-xs" style={{ color: "var(--color-text-secondary)" }}>
            Consent-gated replies · STOP silences automatically
          </p>
        </div>
      </div>
    </AppLayout>
  );
}

function labelFor(field) {
  const labels = {
    "contact.fullName": "Name",
    "contact.email": "Email",
    "contact.phone": "Phone",
    "conversation.next_step": "Suggested next step",
  };
  return labels[field] || field;
}

function displayValue(value) {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") {
    const step = value;
    return [step.action, step.dueAt ? `due ${new Date(step.dueAt).toLocaleDateString("en-IN")}` : null]
      .filter(Boolean)
      .join(" · ");
  }
  return String(value);
}
